import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils';

const segmentedControlVariants = cva('relative border border-border', {
  variants: {
    variant: {
      default: 'gap-0.5 rounded-xl bg-muted p-0.5',
      contrast: 'gap-1 rounded-xl border-2 bg-muted/30 p-1',
      compact: 'gap-0.5 rounded-md bg-muted p-0.5',
    },
    layout: {
      default: 'inline-flex w-fit',
      wrap: 'inline-flex w-fit flex-wrap',
      stretch: 'grid w-full auto-cols-fr grid-flow-col',
    },
  },
  defaultVariants: { variant: 'default', layout: 'default' },
});

// Radii are concentric with the wrapper: wrapper radius minus its padding
// (12px − 2px = 10px for default, 12px − 4px = 8px for contrast; compact relies
// on the themed rounded-md/rounded-sm pair which already differs by 2px). The
// active pill background lives on the sliding indicator; items keep the same
// radius so the focus ring matches.
const segmentedControlItemVariants = cva(
  'group relative z-[1] inline-flex cursor-pointer items-center justify-center whitespace-nowrap text-muted-foreground transition-all duration-150 ease-spring hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'rounded-[10px] font-display font-bold data-[state=on]:text-white data-[state=on]:hover:text-white aria-[current=page]:text-white aria-[current=page]:hover:text-white',
        contrast:
          'rounded-[8px] font-medium data-[state=on]:text-background data-[state=on]:hover:text-background aria-[current=page]:text-background aria-[current=page]:hover:text-background',
        compact:
          'rounded-sm font-semibold data-[state=on]:text-foreground aria-[current=page]:text-foreground',
      },
      size: {
        default: 'px-3 py-1.5 text-[13px]',
        sm: 'px-2.5 py-1 text-xs',
      },
    },
    compoundVariants: [
      { variant: 'contrast', size: 'default', className: 'text-sm' },
      { variant: 'compact', size: 'sm', className: 'py-1.5' },
    ],
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

// Sliding pill behind the active item. Position/size are set imperatively
// (transform + width/height) from the active item's offsets.
const segmentedControlIndicatorVariants = cva(
  'pointer-events-none absolute left-0 top-0 z-0 transition-[transform,width,height] duration-200 ease-spring motion-reduce:transition-none',
  {
    variants: {
      variant: {
        default: 'rounded-[10px] bg-riso-blue shadow-hard-sm',
        contrast: 'rounded-[8px] bg-foreground',
        compact: 'rounded-sm bg-card shadow-hard-sm',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

type SegmentedControlVariant = NonNullable<
  VariantProps<typeof segmentedControlItemVariants>['variant']
>;
type SegmentedControlSize = NonNullable<
  VariantProps<typeof segmentedControlItemVariants>['size']
>;

interface SegmentedControlContextValue {
  value?: string;
  onValueChange?: (value: string) => void;
  variant: SegmentedControlVariant;
  size: SegmentedControlSize;
}

const SegmentedControlContext =
  React.createContext<SegmentedControlContextValue>({
    variant: 'default',
    size: 'default',
  });

export interface SegmentedControlProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof segmentedControlVariants> {
  /** Active item value. Omit for presentational groups (e.g. NavLink items). */
  value?: string;
  onValueChange?: (value: string) => void;
  size?: SegmentedControlSize;
}

/**
 * Pill-style segmented control. Compound API: wrap `SegmentedControlItem`s and
 * either control the group via `value`/`onValueChange`, or leave it
 * uncontrolled and let each item derive its own active state (`active` prop, or
 * `aria-current="page"` from a router NavLink via `asChild`).
 */
const SegmentedControl = React.forwardRef<
  HTMLDivElement,
  SegmentedControlProps
>(
  (
    {
      className,
      variant,
      layout,
      size,
      value,
      onValueChange,
      children,
      ...props
    },
    ref,
  ) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const indicatorRef = React.useRef<HTMLSpanElement | null>(null);
    // False until the indicator has been placed once; the first placement is
    // instant so mount doesn't animate a slide-in from the origin.
    const positionedRef = React.useRef(false);

    const setContainerRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        containerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );

    const updateIndicator = React.useCallback(() => {
      const container = containerRef.current;
      const indicator = indicatorRef.current;
      if (!container || !indicator) return;
      const active = container.querySelector<HTMLElement>(
        '[data-state="on"], [aria-current="page"]',
      );
      if (!active) {
        indicator.style.opacity = '0';
        positionedRef.current = false;
        return;
      }
      const firstPlacement = !positionedRef.current;
      if (firstPlacement) indicator.style.transitionDuration = '0s';
      indicator.style.opacity = '1';
      indicator.style.transform = `translate(${active.offsetLeft}px, ${active.offsetTop}px)`;
      indicator.style.width = `${active.offsetWidth}px`;
      indicator.style.height = `${active.offsetHeight}px`;
      if (firstPlacement) {
        positionedRef.current = true;
        requestAnimationFrame(() => {
          indicator.style.transitionDuration = '';
        });
      }
    }, []);

    // Re-measure on every render (covers value/children changes)…
    React.useLayoutEffect(() => {
      updateIndicator();
    });

    // …plus container resizes (font load, wrap reflow) and attribute flips on
    // items — asChild NavLinks toggle aria-current without re-rendering us.
    React.useLayoutEffect(() => {
      const container = containerRef.current;
      if (!container) return;
      const resizeObserver = new ResizeObserver(updateIndicator);
      resizeObserver.observe(container);
      const mutationObserver = new MutationObserver(updateIndicator);
      mutationObserver.observe(container, {
        subtree: true,
        attributes: true,
        attributeFilter: ['data-state', 'aria-current'],
      });
      return () => {
        resizeObserver.disconnect();
        mutationObserver.disconnect();
      };
    }, [updateIndicator]);

    const ctx = React.useMemo<SegmentedControlContextValue>(
      () => ({
        value,
        onValueChange,
        variant: variant ?? 'default',
        size: size ?? 'default',
      }),
      [value, onValueChange, variant, size],
    );
    return (
      <SegmentedControlContext.Provider value={ctx}>
        <div
          ref={setContainerRef}
          role="group"
          className={cn(
            segmentedControlVariants({ variant, layout }),
            size === 'sm' && 'min-h-9',
            className,
          )}
          {...props}
        >
          <span
            ref={indicatorRef}
            aria-hidden="true"
            style={{ opacity: 0 }}
            className={segmentedControlIndicatorVariants({ variant })}
          />
          {children}
        </div>
      </SegmentedControlContext.Provider>
    );
  },
);
SegmentedControl.displayName = 'SegmentedControl';

export interface SegmentedControlItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Matches the group `value` to become active. */
  value?: string;
  /** Explicit active state; overrides group-value matching. */
  active?: boolean;
  asChild?: boolean;
}

const SegmentedControlItem = React.forwardRef<
  HTMLButtonElement,
  SegmentedControlItemProps
>(({ className, value, active, asChild = false, onClick, ...props }, ref) => {
  const ctx = React.useContext(SegmentedControlContext);
  const isActive =
    active ??
    (value !== undefined && ctx.value !== undefined && ctx.value === value);
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      ref={ref}
      // asChild items (NavLink) carry their own semantics via aria-current
      {...(asChild
        ? {}
        : { type: 'button' as const, 'aria-pressed': isActive })}
      data-state={isActive ? 'on' : 'off'}
      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (value !== undefined) ctx.onValueChange?.(value);
      }}
      className={cn(
        segmentedControlItemVariants({ variant: ctx.variant, size: ctx.size }),
        className,
      )}
      {...props}
    />
  );
});
SegmentedControlItem.displayName = 'SegmentedControlItem';

export {
  SegmentedControl,
  SegmentedControlItem,
  segmentedControlVariants,
  segmentedControlItemVariants,
};
